import React, { useMemo, useState } from 'react';
import { ChartFactory } from '../../../../../ChartUtility/charts';
import { useInsights } from '../../../../../../globalComponents/insights/useInsights';
import InsightsPanel from '../../../../../../globalComponents/insights/InsightsPanel';

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const DENOM_ICONS = { area: '🏢', patient: '🧑‍⚕️', bed: '🛏️', revenue: '💰', employee: '👤' };
const denomIcon = title => Object.entries(DENOM_ICONS).find(([k]) => title?.toLowerCase().includes(k))?.[1] ?? '📊';

const locName = (id, locs) => {
  const l = locs.find(l => Number(l.id) === Number(id));
  return l?.unitCode || l?.location?.city || `Loc ${id}`;
};

// Strips trailing year from period labels (e.g. "Apr 2025" → "Apr") when
// all labels within the range are already unique without it (single-FY view).
const stripYear = label => label.replace(/\s+\d{4}$/, '');

// Returns "2025-26" style FY label for a given date and company starting month.
const getFYLabel = (fromDate, sm) => {
  const d = new Date(fromDate + 'T00:00:00'), m = d.getMonth() + 1, y = d.getFullYear();
  const s = m >= sm ? y : y - 1;
  return `${s}-${String(s + 1).slice(2)}`;
};

// Returns 0 instead of Infinity/NaN when denominator is null or zero.
const ratio = (n, d) => (n != null && d != null && d !== 0) ? n / d : 0;

const sortLimit = (arr, sortBy, limit, valKey, nameKey) => {
  if (sortBy === 'value-desc') arr.sort((a, b) => b[valKey] - a[valKey]);
  else if (sortBy === 'value-asc') arr.sort((a, b) => a[valKey] - b[valKey]);
  else if (nameKey && sortBy === 'name-asc') arr.sort((a, b) => String(a[nameKey]).localeCompare(String(b[nameKey])));
  return limit ? arr.slice(0, limit) : arr;
};

// Builds a Map keyed by "locationId|FY" with summed numerator and denominator.
// Used by both X=Location,Y=FY and X=FY,Y=Location chart combinations.
const buildLocFyMap = (row, denom, sm) => {
  const denomPerLoc = denom.timePeriods.some(t => t.locationId != null);
  const map = new Map();
  for (const tp of row.timePeriods) {
    if (tp.locationId == null) continue;
    const key = `${tp.locationId}|${getFYLabel(tp.fromDate, sm)}`;
    if (!map.has(key)) map.set(key, { locId: tp.locationId, fy: getFYLabel(tp.fromDate, sm), numSum: 0, denSum: 0 });
    map.get(key).numSum += tp.value;
  }
  for (const tp of denom.timePeriods) {
    const fy = getFYLabel(tp.fromDate, sm);
    if (denomPerLoc && tp.locationId != null) {
      const e = map.get(`${tp.locationId}|${fy}`); if (e) e.denSum += tp.value;
    } else if (!denomPerLoc) {
      for (const [k, v] of map) { if (k.endsWith(`|${fy}`)) v.denSum += tp.value; }
    }
  }
  return map;
};

// Converts raw timePeriods into ChartFactory-compatible data.
// Always sums numerator and denominator separately per bucket before dividing —
// never average a pre-computed ratio.
const buildChartData = (row, denom, allPeriods, locations, xAxis, yAxis, sortBy, limit, sm = 4) => {
  const unitLabel   = row.unit && denom.unit ? `${row.unit} / ${denom.unit}` : row.unit || denom.unit || '';
  const seriesLabel = `${row.label} per ${denom.title}`;

  // ── X = Period ──────────────────────────────────────────────────────────────
  if (xAxis === 'period') {
    const shortLabels = allPeriods.map(p => stripYear(p.label));
    const useShort    = shortLabels.length === new Set(shortLabels).size;
    const pLabel      = p => useShort ? stripYear(p.label) : p.label;

    if (yAxis === 'none') {
      const withIdx = allPeriods.map((p, i) => ({
        displayPeriods: pLabel(p), kpi: seriesLabel, unit: unitLabel, _idx: i,
        sum_value: ratio(row.timePeriods.find(t => t.period === p.label)?.value ?? null, denom.timePeriods.find(t => t.period === p.label)?.value ?? null),
      }));
      const sorted = sortLimit([...withIdx], sortBy, limit, 'sum_value', 'displayPeriods');
      if (sortBy === 'name-asc') sorted.sort((a, b) => a._idx - b._idx);
      return sorted.map(({ _idx, ...r }) => r);
    }

    if (yAxis === 'location') {
      const locIds  = [...new Set(row.timePeriods.filter(t => t.locationId != null).map(t => t.locationId))];
      const allData = locIds.flatMap(locId => allPeriods.map(p => {
        // eslint-disable-next-line eqeqeq
        const n = row.timePeriods.find(t => t.period === p.label && t.locationId == locId)?.value ?? null;
        // eslint-disable-next-line eqeqeq
        const d = (denom.timePeriods.find(t => t.period === p.label && t.locationId == locId) ?? denom.timePeriods.find(t => t.period === p.label))?.value ?? null;
        return { displayPeriods: pLabel(p), kpi: locName(locId, locations), _locId: locId, sum_value: ratio(n, d) };
      }));
      const kept = new Set(sortLimit(
        locIds.map(locId => ({ name: locName(locId, locations), total: allData.filter(d => d._locId === locId).reduce((s, d) => s + d.sum_value, 0) })),
        sortBy, limit, 'total', 'name'
      ).map(l => l.name));
      return allData.filter(d => kept.has(d.kpi)).map(({ _locId, ...r }) => r);
    }

    if (yAxis === 'financial-year') {
      const map = new Map();
      const add = (tp, field) => {
        const key = `${getFYLabel(tp.fromDate, sm)}|${MONTHS_SHORT[new Date(tp.fromDate + 'T00:00:00').getMonth()]}`;
        if (!map.has(key)) { const [fy, mon] = key.split('|'); map.set(key, { numSum: 0, denSum: 0, fy, mon }); }
        map.get(key)[field] += tp.value;
      };
      for (const tp of row.timePeriods)   add(tp, 'numSum');
      for (const tp of denom.timePeriods) add(tp, 'denSum');
      const ranking = sortLimit(
        [...new Set([...map.values()].map(v => v.fy))].sort().map(fy => ({ fyLabel: fy, total: [...map.values()].filter(v => v.fy === fy).reduce((s, v) => s + ratio(v.numSum, v.denSum), 0) })),
        sortBy, limit, 'total', 'fyLabel'
      );
      const months = Array.from({ length: 12 }, (_, i) => MONTHS_SHORT[(sm - 1 + i) % 12]);
      return months.flatMap(mon => ranking.map(({ fyLabel }) => {
        const e = map.get(`${fyLabel}|${mon}`);
        return { displayPeriods: mon, kpi: fyLabel, sum_value: e ? ratio(e.numSum, e.denSum) : 0 };
      }));
    }
  }

  // ── X=Location↔FY with Y=FY↔Location (shared via xIsLoc flag) ──────────────
  if ((xAxis === 'location' && yAxis === 'financial-year') || (xAxis === 'financial-year' && yAxis === 'location')) {
    const xIsLoc   = xAxis === 'location';
    const locFyMap = buildLocFyMap(row, denom, sm);
    const fyLabels = [...new Set([...locFyMap.values()].map(v => v.fy))].sort();
    const locIds   = [...new Set([...locFyMap.values()].map(v => v.locId))];
    const ranking  = sortLimit(
      // eslint-disable-next-line eqeqeq
      locIds.map(locId => ({ locId, name: locName(locId, locations), total: [...locFyMap.values()].filter(v => v.locId == locId).reduce((s, v) => s + ratio(v.numSum, v.denSum), 0) })).filter(l => l.total > 0),
      sortBy, limit, 'total', 'name'
    );
    return ranking.flatMap(({ locId, name }) => fyLabels.map(fy => {
      const e = locFyMap.get(`${locId}|${fy}`);
      return { displayPeriods: xIsLoc ? name : fy, kpi: xIsLoc ? fy : name, sum_value: e ? ratio(e.numSum, e.denSum) : 0 };
    }));
  }

  // ── X=Location, Y=None ──────────────────────────────────────────────────────
  if (xAxis === 'location') {
    const locIds      = [...new Set(row.timePeriods.filter(t => t.locationId != null).map(t => t.locationId))];
    const denomPerLoc = denom.timePeriods.some(t => t.locationId != null);
    const ranked = sortLimit(
      locIds.map(locId => {
        // eslint-disable-next-line eqeqeq
        const locNum = row.timePeriods.filter(t => t.locationId == locId);
        // eslint-disable-next-line eqeqeq
        const locDen = denomPerLoc ? denom.timePeriods.filter(t => t.locationId == locId) : null;
        let numSum = 0, denSum = 0;
        for (const tp of locNum) {
          // Only pair periods that exist in BOTH num and den to avoid inflated ratios on sparse data.
          const de = locDen ? locDen.find(d => d.period === tp.period) : denom.timePeriods[0];
          if (locDen) { if (de) { numSum += tp.value; denSum += de.value; } }
          else { numSum += tp.value; }
        }
        if (!locDen) denSum = denom.timePeriods.reduce((s, t) => s + t.value, 0);
        return { name: locName(locId, locations), ratio: ratio(numSum, denSum) };
      }).filter(l => l.ratio > 0),
      sortBy, limit, 'ratio', 'name'
    );
    return ranked.map(l => ({ displayPeriods: l.name, kpi: seriesLabel, unit: unitLabel, sum_value: l.ratio }));
  }

  // ── X=FY, Y=None ────────────────────────────────────────────────────────────
  if (xAxis === 'financial-year') {
    const map = new Map();
    const add = (tp, field) => { const k = getFYLabel(tp.fromDate, sm); if (!map.has(k)) map.set(k, { numSum: 0, denSum: 0 }); map.get(k)[field] += tp.value; };
    for (const tp of row.timePeriods)   add(tp, 'numSum');
    for (const tp of denom.timePeriods) add(tp, 'denSum');
    return sortLimit(
      [...map.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([fy, { numSum, denSum }]) => ({ displayPeriods: fy, kpi: seriesLabel, unit: unitLabel, sum_value: ratio(numSum, denSum) })),
      sortBy, limit, 'sum_value', 'displayPeriods'
    );
  }

  return [];
};

export const IntensityDenomCard = ({ row, denom, allPeriods, locations = [], isTopLevel, xAxis = 'period', yAxis = 'none', sortBy = 'value-desc', limit = null, startingMonth = 4, chartHeight = 320, chartOverride = 'line' }) => {
  const accent    = isTopLevel ? '#3f88a7' : '#10b981';
  const unitLabel = row.unit && denom.unit ? `${row.unit} / ${denom.unit}` : row.unit || denom.unit || '';

  const data = useMemo(
    () => buildChartData(row, denom, allPeriods, locations, xAxis, yAxis, sortBy, limit, startingMonth),
    [row, denom, allPeriods, locations, xAxis, yAxis, sortBy, limit, startingMonth], // eslint-disable-line
  );

  const hasData    = data.some(d => d.sum_value !== 0);
  const numSeries  = new Set(data.map(d => d.kpi)).size;
  const effectiveH = numSeries > 10 ? Math.max(chartHeight, 420) : numSeries > 4 ? Math.max(chartHeight, 320) : chartHeight;

  return (
    <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
      <div style={{ height: 3, background: accent }} />
      <div style={{ padding: '14px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <span style={{ fontSize: 16 }}>{denomIcon(denom.title)}</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#111827', flex: 1 }}>{row.label} per {denom.title}</span>
          {hasData && unitLabel && (
            <span style={{ fontSize: 11, color: '#92400e', background: '#fef3c7', borderRadius: 6, padding: '2px 8px', fontWeight: 600, border: '1px solid #fde68a', whiteSpace: 'nowrap' }}>
              {unitLabel}
            </span>
          )}
        </div>
        {hasData ? (
          <ChartFactory
            chartData={{ data, chartType: chartOverride }}
            chartOptions={{ height: effectiveH, stacked: false, transform: true }}
            group_by={['displayPeriods', 'kpi']}
          />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: chartHeight, color: '#cbd5e1', fontSize: 13 }}>
            No data available
          </div>
        )}
      </div>
    </div>
  );
};

// Renders a chart card paired with a separate insights card side by side.
// Used when the Insights toggle is on — mirrors the layout in the Energy dashboard.
const IntensityDenomPair = ({ row, denom, allPeriods, locations, isTopLevel, xAxis, yAxis, sortBy, limit, startingMonth, chartHeight = 320, chartOverride = 'line' }) => {
  const accent = isTopLevel ? '#3f88a7' : '#10b981';

  const data = useMemo(
    () => buildChartData(row, denom, allPeriods, locations, xAxis, yAxis, sortBy, limit, startingMonth),
    [row, denom, allPeriods, locations, xAxis, yAxis, sortBy, limit, startingMonth], // eslint-disable-line
  );

  const hasData   = data.some(d => d.sum_value !== 0);
  const numSeries = new Set(data.map(d => d.kpi)).size;
  const effectiveH = numSeries > 10 ? Math.max(chartHeight, 420) : numSeries > 4 ? Math.max(chartHeight, 320) : chartHeight;

  const graphId   = useMemo(() => `intensity::${row.label}::${denom.title}`, [row.label, denom.title]);
  const graphData = useMemo(() => ({ data }), [data]); // eslint-disable-line
  const meta      = useMemo(() => ({ title: `${row.label} / ${denom.title}`, chartType: chartOverride }), [row.label, denom.title, chartOverride]);

  const { insights, loading, error, refresh } = useInsights('intensity', graphId, graphData, meta, hasData);

  // Card header (~56px) + chart padding (~28px) added so the insights card
  // matches the full visible height of the chart card.
  const insightsH = effectiveH + 84;

  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      <div style={{ flex: '1 1 0', minWidth: 0 }}>
        <IntensityDenomCard
          row={row} denom={denom} allPeriods={allPeriods} locations={locations}
          isTopLevel={isTopLevel} xAxis={xAxis} yAxis={yAxis}
          sortBy={sortBy} limit={limit} startingMonth={startingMonth} chartHeight={chartHeight} chartOverride={chartOverride}
        />
      </div>
      <div style={{ flex: '1 1 0', minWidth: 0, height: insightsH }}>
        <InsightsPanel insights={insights} loading={loading} error={error} onRefresh={refresh} accent={accent} />
      </div>
    </div>
  );
};

export const IntensityRowSection = ({ row, denomData, allPeriods, locations = [], xAxis, yAxis, sortBy, limit, startingMonth = 4, chartOverride = 'line', defaultCollapsed = false, insightsEnabled = false }) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const isTopLevel  = row.type === 'topLevel';
  const borderColor = isTopLevel ? '#3f88a7' : '#10b981';
  const badgeStyle  = { fontSize: 11, fontWeight: 700, borderRadius: 20, padding: '2px 9px', background: isTopLevel ? '#dbeafe' : '#d1fae5', color: isTopLevel ? '#1d4ed8' : '#065f46' };

  return (
    <div style={{ marginBottom: 12, borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden', background: '#fff' }}>
      <div onClick={() => setCollapsed(c => !c)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', cursor: 'pointer', borderLeft: `4px solid ${borderColor}`, background: collapsed ? '#f8fafc' : '#fff', userSelect: 'none' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{row.label}</span>
            <span style={badgeStyle}>{isTopLevel ? 'Top level' : 'KPI level'}</span>
            {row.unit && <span style={{ fontSize: 11, color: '#6b21a8', background: '#f3e8ff', borderRadius: 4, padding: '1px 6px', fontWeight: 500 }}>{row.unit}</span>}
          </div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{row.unit || 'value'} ÷ denominator</div>
        </div>
        <span style={{ fontSize: 13, color: '#94a3b8', display: 'inline-block', transform: collapsed ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>∧</span>
      </div>

      {!collapsed && (
        insightsEnabled ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: 16 }}>
            {denomData.map(denom => (
              <IntensityDenomPair key={denom.title} row={row} denom={denom} allPeriods={allPeriods} locations={locations} isTopLevel={isTopLevel} xAxis={xAxis} yAxis={yAxis} sortBy={sortBy} limit={limit} startingMonth={startingMonth} chartOverride={chartOverride} />
            ))}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: denomData.length === 1 ? '1fr' : '1fr 1fr', gap: 16, padding: 16 }}>
            {denomData.map(denom => (
              <IntensityDenomCard key={denom.title} row={row} denom={denom} allPeriods={allPeriods} locations={locations} isTopLevel={isTopLevel} xAxis={xAxis} yAxis={yAxis} sortBy={sortBy} limit={limit} startingMonth={startingMonth} chartOverride={chartOverride} />
            ))}
            {denomData.length % 2 !== 0 && denomData.length > 1 && <div />}
          </div>
        )
      )}
    </div>
  );
};
